import { useTranslation } from 'react-i18next';
import { Heading, Paragraph } from '@digdir/designsystemet-react';
import { formatScore, TeamFilter, TeamStatus } from '../hrPsychologicalSafetyView';
import classes from '../HRPsychologicalSafety.module.css';

type TeamDistributionProps = {
  counts: Record<TeamStatus, number>;
  total: number;
  averageScore: number;
  onShowTeams: (filter: TeamFilter) => void;
};

const ORDER: TeamStatus[] = ['danger', 'warning', 'success', 'none'];

/** Hvor mange team som er utfordrende, moderate, gode eller uten resultater */
export const TeamDistribution = ({ counts, total, averageScore, onShowTeams }: TeamDistributionProps) => {
  const { t, i18n } = useTranslation();
  const percent = (count: number) => (total > 0 ? Math.round((count / total) * 100) : 0);
  const hasResults = total - counts.none > 0;

  return (
    <section aria-labelledby="hr-ps-distribution" className={classes.card}>
      <div className={classes.sectionIntro}>
        <Heading level={2} data-size="md" id="hr-ps-distribution">
          {t('hrPsychologicalSafety.distribution.title')}
        </Heading>
        <Paragraph data-size="sm" className={classes.subtle}>
          {hasResults
            ? t('hrPsychologicalSafety.distribution.intro', {
                count: total,
                score: formatScore(averageScore, i18n.language),
              })
            : t('hrPsychologicalSafety.distribution.introNoResults', { count: total })}
        </Paragraph>
      </div>
      <div
        role="img"
        aria-label={t('hrPsychologicalSafety.distribution.aria', {
          total,
          danger: counts.danger,
          warning: counts.warning,
          success: counts.success,
          none: counts.none,
        })}
        className={classes.distributionBar}
      >
        {ORDER.filter((status) => counts[status] > 0).map((status) => (
          <span key={status} data-status={status} style={{ flexGrow: counts[status] }} />
        ))}
      </div>
      <ul className={classes.distributionTiles}>
        {ORDER.map((status) => (
          <li key={status}>
            <button
              type="button"
              className={classes.distributionTile}
              data-status={status}
              onClick={() => onShowTeams(status === 'none' ? 'missing' : 'results')}
            >
              <span className={classes.distributionLabel}>
                <span aria-hidden="true" className={classes.dot} data-status={status} />
                {t(`hrPsychologicalSafety.status.${status}`)}
              </span>
              <span className={classes.distributionValue}>
                <span className={classes.distributionCount}>{counts[status]}</span>{' '}
                {t('hrPsychologicalSafety.distribution.teams', {
                  count: counts[status],
                  percent: percent(counts[status]),
                })}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};
